import React, { useState, useRef } from 'react';
import { 
  Container, 
  Typography, 
  Paper, 
  Box, 
  Grid,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  List,
  ListItem,
  Chip,
  IconButton,
  Tooltip,
  Divider
} from '@mui/material';
import { 
  Description as DescriptionIcon,
  Article as ArticleIcon,
  CloudUpload as CloudUploadIcon,
  ArrowBack as ArrowBackIcon,
  Download as DownloadIcon,
  Email as EmailIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import PacFormPage from './PacFormPage';
import ConfirmationPage from './ConfirmationPage';
import EmailDialog from '../signature/EmailDialog';
import HomeConfirmationPage from './HomeConfirmationPage';

const INSURANCE_COMPANIES = [
  { id: 'intact', name: 'Intact Insurance' },
  { id: 'wawanesa', name: 'Wawanesa Insurance' },
  { id: 'economical', name: 'Economical Insurance' },
  { id: 'gore_mutual', name: 'Gore Mutual Insurance' },
  { id: 'travelers', name: 'Travelers Insurance' }
];

const DocumentCard = ({ title, subtitle, description, onClick }) => (
  <Paper
    elevation={2}
    sx={{
      p: 2,
      height: '100%',
      cursor: 'pointer',
      transition: 'transform 0.2s, box-shadow 0.2s',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: '#f8f9fa',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: 3,
      }
    }}
    onClick={onClick}
  >
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%',
      minHeight: '180px',
      border: '2px dashed #1976d2',
      borderRadius: 2,
      backgroundColor: 'rgba(25, 118, 210, 0.04)',
      transition: 'all 0.2s ease',
      '&:hover': {
        backgroundColor: 'rgba(25, 118, 210, 0.08)',
      }
    }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1.5
      }}>
        <CloudUploadIcon sx={{ fontSize: 40, color: '#1976d2' }} />
        <Typography variant="h6" sx={{ color: '#1976d2' }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="subtitle2" color="text.secondary">
            {subtitle}
          </Typography>
        )}
        <Typography 
          variant="body2" 
          color="text.secondary" 
          sx={{ 
            textAlign: 'center', 
            maxWidth: '90%',
            fontSize: '0.875rem'
          }}
        >
          {description}
        </Typography>
      </Box>
    </Box>
  </Paper>
);

const DocumentGeneratorPage = () => {
  const navigate = useNavigate();
  const [selectedDocType, setSelectedDocType] = useState('');
  const [file, setFile] = useState(null);
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const [openPacFormDialog, setOpenPacFormDialog] = useState(false);
  const [pacFormData, setPacFormData] = useState({});
  const [openEmailDialog, setOpenEmailDialog] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);
  const [openHomeConfirmationDialog, setOpenHomeConfirmationDialog] = useState(false);

  const handleDocTypeSelect = (docType) => {
    setSelectedDocType(docType);
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (event) => {
    const uploadedFile = event.target.files[0];
    if (!uploadedFile) return;
    
    if (uploadedFile.type === 'application/pdf') {
      setFile(uploadedFile);
      
      if (selectedDocType === 'auto_insurance_confirmation') {
        setOpenConfirmationDialog(true);
      } else if (selectedDocType === 'home_insurance_confirmation') {
        setOpenHomeConfirmationDialog(true);
      } else if (selectedDocType === 'pac_form') {
        const extractedBankData = {
          accountHolder: 'John Smith',
          bankName: 'TD Canada Trust',
          transitNumber: '12345',
          institutionNumber: '004',
          accountNumber: '1234567',
        };
        setPacFormData(extractedBankData);
        setOpenPacFormDialog(true);
      }
    } else {
      alert('Please upload a PDF file');
    }
    event.target.value = '';
  };

  // 修改示例文档列表数据
  const generatedDocuments = [
    {
      id: 1,
      name: 'Auto Insurance Confirmation.pdf',
      documentType: 'Auto',
      size: '2.4 MB',
      createdAt: '2024-01-22',
    },
    {
      id: 2,
      name: 'Home Insurance Confirmation.pdf',
      documentType: 'Home',
      size: '1.8 MB',
      createdAt: '2024-01-21',
    },
    {
      id: 3,
      name: 'Pre-Authorized Payment Form.pdf',
      documentType: 'Payment',
      size: '1.2 MB',
      createdAt: '2024-01-20',
    }
  ];

  const handleDownload = (document) => {
    console.log('Downloading:', document.name);
  };

  const handleEmail = (document) => {
    setSelectedDocument(document);
    setOpenEmailDialog(true);
  };

  const handleDelete = (document) => {
    // Implement the delete logic here
    console.log('Deleting:', document.name);
  };

  const handlePreview = (doc) => {
    // 在新窗口打开文档预览页面
    const previewUrl = `/generate/document-preview/${doc.id}`;
    const documentState = { document: doc };
    
    // 将 state 数据编码并作为 URL 参数传递
    const stateParam = encodeURIComponent(JSON.stringify(documentState));
    const fullUrl = `${previewUrl}?state=${stateParam}`;
    
    // 使用 window.open 在新窗口打开
    window.open(fullUrl, '_blank');
  };

  return (
    <Container maxWidth="xl">
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf"
        hidden
        onChange={handleFileSelect}
      />

      {/* Hero Section */}
      <Box sx={{ 
        pt: 4,
        pb: 3,
        textAlign: 'center',
        position: 'relative',
        borderBottom: '1px solid #e0e0e0'
      }}>
        <Button
          variant="outlined"
          onClick={() => navigate('/')}
          startIcon={<ArrowBackIcon />}
          sx={{
            position: 'absolute',
            left: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#1976d2',
            borderColor: '#1976d2',
            '&:hover': {
              borderColor: '#1565c0',
              backgroundColor: 'rgba(25, 118, 210, 0.04)'
            }
          }}
        >
          Back to Home
        </Button>

        <Typography 
          variant="h4" 
          sx={{ 
            color: '#1976d2',
            fontWeight: 600,
            mb: 1
          }}
        >
          Document Generator
        </Typography>
        <Typography 
          variant="subtitle1" 
          sx={{ 
            color: '#666',
            maxWidth: '600px',
            mx: 'auto'
          }}
        >
          Upload your PDF document and let our system process it automatically
        </Typography>
      </Box>

      {/* Document Generation Section */}
      <Paper elevation={3} sx={{ p: 3, borderRadius: 3, backgroundColor: '#ffffff' }}>
        <Typography variant="h5" sx={{ mb: 3, color: '#1565c0', fontWeight: 500 }}>
          Document Generation
        </Typography>

        <Grid container spacing={3}>
          {/* Auto Insurance Section */}
          <Grid item xs={12} md={4}>
            <Box sx={{ height: '100%' }}>
              <DocumentCard
                title="Auto Insurance Confirmation"
                description="Upload your auto insurance application PDF"
                onClick={() => handleDocTypeSelect('auto_insurance_confirmation')}
              />
            </Box>
          </Grid>

          {/* Home Insurance Section */}
          <Grid item xs={12} md={4}>
            <Box sx={{ height: '100%' }}>
              <DocumentCard
                title="Home Insurance Confirmation"
                description="Upload your home insurance application PDF"
                onClick={() => handleDocTypeSelect('home_insurance_confirmation')}
              />
            </Box>
          </Grid>

          {/* PAC Form Section */}
          <Grid item xs={12} md={4}>
            <Box sx={{ height: '100%' }}>
              <DocumentCard
                title="PAC Form Generator"
                description="Upload your void cheque PDF"
                onClick={() => handleDocTypeSelect('pac_form')}
              />
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Dialogs */}
      {/* Auto Insurance Confirmation Dialog */}
      <Dialog
        open={openConfirmationDialog}
        onClose={() => setOpenConfirmationDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <ConfirmationPage
          docType="auto_insurance_confirmation"
          file={file}
          onClose={() => setOpenConfirmationDialog(false)}
        />
      </Dialog>

      {/* Home Insurance Confirmation Dialog */}
      <Dialog
        open={openHomeConfirmationDialog}
        onClose={() => setOpenHomeConfirmationDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <HomeConfirmationPage
          docType="home_insurance_confirmation"
          file={file}
          onClose={() => setOpenHomeConfirmationDialog(false)}
        />
      </Dialog>

      {/* PAC Form Dialog */}
      <Dialog
        open={openPacFormDialog}
        onClose={() => setOpenPacFormDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <PacFormPage
          initialData={pacFormData}
          onClose={() => setOpenPacFormDialog(false)}
          onSubmit={async (formData) => {
            setLoading(true);
            try {
              const submitData = {
                ...formData,
                voidCheque: file
              };
              
              // 模拟API调用
              await new Promise(resolve => setTimeout(resolve, 2000));
              
              alert('PAC Form generated successfully!');
              setOpenPacFormDialog(false);
            } catch (error) {
              console.error('Failed to generate PAC Form:', error);
              alert('Failed to generate PAC Form. Please try again.');
            } finally {
              setLoading(false);
            }
          }}
        />
      </Dialog>

      {/* Loading Dialog */}
      <Dialog open={loading}>
        <DialogContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CircularProgress />
          <Typography>Generating Document...</Typography>
        </DialogContent>
      </Dialog>

      {/* Generated Documents Section */}
      <Paper elevation={3} sx={{ p: 3, mt: 4, borderRadius: 3, backgroundColor: '#ffffff' }}>
        <Typography variant="h5" sx={{ mb: 3, color: '#1565c0', fontWeight: 500 }}>
          Generated Documents
        </Typography>

        <Box sx={{ backgroundColor: '#f8f9fa', borderRadius: 2, overflow: 'hidden' }}>
          {/* Table Header */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 3,
            p: 2,
            borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
            backgroundColor: '#fff'
          }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" color="text.secondary" fontWeight={500}>
                Document Name
              </Typography>
            </Box>
            <Box sx={{ width: 100, textAlign: 'center' }}>
              <Typography variant="subtitle2" color="text.secondary" fontWeight={500}>
                Type
              </Typography>
            </Box>
            <Box sx={{ width: 120, textAlign: 'center' }}>
              <Typography variant="subtitle2" color="text.secondary" fontWeight={500}>
                Date
              </Typography>
            </Box>
            <Box sx={{ width: 100, textAlign: 'center' }}>
              <Typography variant="subtitle2" color="text.secondary" fontWeight={500}>
                Size
              </Typography>
            </Box>
            <Box sx={{ width: 140, textAlign: 'center' }}>
              <Typography variant="subtitle2" color="text.secondary" fontWeight={500}>
                Actions
              </Typography>
            </Box>
          </Box>

          {/* Document List */}
          <List sx={{ p: 0 }}>
            {generatedDocuments.map((doc, index) => (
              <React.Fragment key={doc.id}>
                <ListItem
                  sx={{
                    py: 2,
                    px: 2,
                    backgroundColor: 'white',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.01)',
                    },
                  }}
                >
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    width: '100%',
                    gap: 3
                  }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1">
                        {doc.name}
                      </Typography>
                    </Box>

                    <Box sx={{ width: 100, textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        {doc.documentType}
                      </Typography>
                    </Box>

                    <Box sx={{ width: 120, textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        {doc.createdAt}
                      </Typography>
                    </Box>

                    <Box sx={{ width: 100, textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        {doc.size}
                      </Typography>
                    </Box>

                    <Box sx={{ width: 140, display: 'flex', justifyContent: 'center', gap: 1 }}>
                      <IconButton
                        size="small"
                        onClick={() => handlePreview(doc)}
                        sx={{ color: '#1976d2' }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleEmail(doc)}
                        sx={{ color: '#1976d2' }}
                      >
                        <EmailIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(doc)}
                        sx={{ color: '#d32f2f' }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                </ListItem>
                {index < generatedDocuments.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Box>
      </Paper>

      {/* 邮件发送对话框 */}
      <EmailDialog
        open={openEmailDialog}
        onClose={() => setOpenEmailDialog(false)}
        transaction={{
          recipients: '',  // 可以根据需要设置默认收件人
          documents: selectedDocument ? [selectedDocument] : []
        }}
      />
    </Container>
  );
};

export default DocumentGeneratorPage; 